/**
 * The handful of places a walkthrough is allowed to point at.
 *
 * An anchor is a `data-tour` attribute on a real element, and this list is the
 * whole vocabulary — so a step cannot name something nobody put on screen, and
 * deleting a component that carries one fails the build rather than quietly
 * producing a tour that points at nothing.
 *
 * Nav entries are the exception: their ids are derived from the route so the
 * sidebar does not need twenty attributes hand-written into it. `registry.test.ts`
 * checks each one against `NAV`, which is the same guarantee a step later.
 */
export const ANCHORS = [
  'shell.nav',
  'shell.bell',
  'shell.guide',
  'shell.account',
  'shell.language',
  'shell.theme',
  'home.banner',
  'home.tiles',
  'home.quiet',
  'page.header',
  'page.search',
  'page.list',
  'cart.actions',
  'items.add',
  'settings.advanced',
  'guide.chapters',
] as const

type KnownAnchor = (typeof ANCHORS)[number]

/**
 * `nav:/shop` and friends. Left as a template rather than a closed union because
 * the route list already exists in `nav.ts` and copying it here would give two
 * lists to keep in step instead of one.
 */
export type NavAnchor = `nav:${string}`

export type AnchorId = KnownAnchor | NavAnchor

/** Spread onto the element the tour should be able to find: `<div {...anchor('home.tiles')}>`. */
export const anchor = (id: AnchorId) => ({ 'data-tour': id }) as const

export const navAnchor = (to: string): NavAnchor => `nav:${to}`

/**
 * The *visible* element carrying an anchor, which is not always the first one.
 *
 * The sidebar and the phone tab bar carry the same nav ids, and the one that is
 * hidden is hidden with `display:none` rather than removed — so it is still in the
 * document, still matches, and reports a zero box. Picking the first match would
 * point the spotlight at the top-left corner on a phone.
 */
export function findAnchor(id: AnchorId): HTMLElement | null {
  const matches = document.querySelectorAll<HTMLElement>(`[data-tour="${CSS.escape(id)}"]`)
  for (const element of matches) {
    const rect = element.getBoundingClientRect()
    if (rect.width > 0 && rect.height > 0) return element
  }
  return null
}
